import { Controller } from "stimulus"

export default class extends Controller {
  static targets = ["canvas", "clearButton", "signatureData", "capturedMessage", "pendingMessage", "capturedTime"]
  
  connect() {
    console.log("Signature Pad controller connected")
    this.initializeCanvas()
  }
  
  initializeCanvas() {
    if (!this.hasCanvasTarget) return
    
    this.canvas = this.canvasTarget
    this.ctx = this.canvas.getContext('2d')
    this.isDrawing = false
    this.hasSignature = false
    
    // Set canvas size
    this.resizeCanvas()
    
    // Event listeners
    this.canvas.addEventListener('mousedown', this.startDrawing.bind(this))
    this.canvas.addEventListener('mousemove', this.draw.bind(this))
    this.canvas.addEventListener('mouseup', this.stopDrawing.bind(this))
    this.canvas.addEventListener('mouseleave', this.stopDrawing.bind(this))
    
    // Touch events for mobile
    this.canvas.addEventListener('touchstart', this.handleTouch.bind(this))
    this.canvas.addEventListener('touchmove', this.handleTouch.bind(this))
    this.canvas.addEventListener('touchend', this.stopDrawing.bind(this))
    
    // Load existing signature if present
    this.loadExistingSignature()
  }
  
  resizeCanvas() {
    const rect = this.canvas.getBoundingClientRect()
    this.canvas.width = rect.width
    this.canvas.height = rect.height
    
    console.log('Canvas resized:', { width: this.canvas.width, height: this.canvas.height })
    
    // Set drawing styles
    this.ctx.strokeStyle = '#000'
    this.ctx.lineWidth = 2
    this.ctx.lineCap = 'round'
    this.ctx.lineJoin = 'round'
  }
  
  startDrawing(e) {
    console.log('startDrawing called')
    this.isDrawing = true
    this.hasSignature = true
    const pos = this.getPosition(e)
    console.log('Start position:', pos)
    this.ctx.beginPath()
    this.ctx.moveTo(pos.x, pos.y)
  }
  
  draw(e) {
    if (!this.isDrawing) return
    
    e.preventDefault()
    const pos = this.getPosition(e)
    console.log('Drawing at:', pos)
    this.ctx.lineTo(pos.x, pos.y)
    this.ctx.stroke()
  }
  
  stopDrawing() {
    if (this.isDrawing) {
      this.isDrawing = false
      this.saveSignature()
    }
  }
  
  handleTouch(e) {
    e.preventDefault()
    const touch = e.touches[0]
    const mouseEvent = new MouseEvent(e.type === 'touchstart' ? 'mousedown' : 'mousemove', {
      clientX: touch.clientX,
      clientY: touch.clientY
    })
    this.canvas.dispatchEvent(mouseEvent)
  }
  
  getPosition(e) {
    const rect = this.canvas.getBoundingClientRect()
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    }
  }
  
  clear() {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
    this.hasSignature = false
    
    if (this.hasSignatureDataTarget) {
      this.signatureDataTarget.value = ''
      // Trigger change event so completion calculation updates
      this.signatureDataTarget.dispatchEvent(new Event('change', { bubbles: true }))
      console.log('Signature cleared and change event dispatched')
    }
    
    // Toggle messages
    if (this.hasCapturedMessageTarget) {
      this.capturedMessageTarget.classList.add('d-none')
    }
    if (this.hasPendingMessageTarget) {
      this.pendingMessageTarget.classList.remove('d-none')
    }
  }
  
  saveSignature() {
    if (!this.hasSignature) {
      console.log('saveSignature: No signature to save')
      return
    }
    
    const dataURL = this.canvas.toDataURL('image/png')
    console.log('saveSignature: Generated dataURL length:', dataURL.length)
    
    if (this.hasSignatureDataTarget) {
      this.signatureDataTarget.value = dataURL
      console.log('saveSignature: Saved to hidden field, value length:', this.signatureDataTarget.value.length)
      // Trigger change event so completion calculation picks it up
      this.signatureDataTarget.dispatchEvent(new Event('change', { bubbles: true }))
      console.log('Signature saved and change event dispatched')
    } else {
      console.error('saveSignature: No signatureData target found!')
    }
    
    // Toggle messages
    if (this.hasCapturedMessageTarget) {
      this.capturedMessageTarget.classList.remove('d-none')
      // Update timestamp
      if (this.hasCapturedTimeTarget) {
        const now = new Date()
        const formattedTime = now.toLocaleString('en-US', { 
          month: 'long', 
          day: 'numeric', 
          year: 'numeric', 
          hour: 'numeric', 
          minute: '2-digit', 
          hour12: true 
        })
        this.capturedTimeTarget.textContent = `on ${formattedTime}`
      }
    }
    if (this.hasPendingMessageTarget) {
      this.pendingMessageTarget.classList.add('d-none')
    }
  }
  
  loadExistingSignature() {
    if (!this.hasSignatureDataTarget) return
    
    const existingData = this.signatureDataTarget.value
    if (!existingData) return
    
    const img = new Image()
    img.onload = () => {
      this.ctx.drawImage(img, 0, 0)
      this.hasSignature = true
    }
    img.src = existingData
  }
}

